import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { STAGE_LABELS } from '../lib/stages';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { GuideContent } from './CallGuideCard';
import {
  PhoneIncoming, Loader2, ArrowLeft, Sparkles, RefreshCw, User, MapPin, Send, Search, NotebookPen,
  ExternalLink, History, Briefcase,
} from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return null; }
};
const fmtM = (v) => (typeof v === 'number' ? `${v.toFixed(1).replace('.', ',')} Mio. €` : null);
const DEAL_TYPE_LABELS = { brokered: 'Brokered', proprietary: 'Proprietary' };

function useInboundSearch(q) {
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.inboundCallSearch(q.trim())
        .then(r => setResults(r.data.results || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return { results, searching };
}

function ResultRow({ r, i, onSelect, big = false }) {
  return (
    <button onClick={() => onSelect(r)} data-testid={`inbound-result-${i}`}
      className={`w-full text-left px-3 ${big ? 'py-3.5' : 'py-2.5'} hover:bg-secondary/50 active:bg-secondary/70 border-b border-border/40 last:border-b-0 transition-colors`}>
      <p className={`${big ? 'text-sm' : 'text-xs'} text-foreground font-medium truncate`}>{r.company_name}</p>
      <p className="text-[11px] text-muted-foreground truncate mt-0.5">
        {r.kind === 'project' && <span className="text-violet-400/80">Brokered · </span>}
        {[r.location, r.sector, STAGE_LABELS[r.pipeline_stage]].filter(Boolean).join(' · ')
          || (r.kind === 'project' ? 'Teaser als Wissensbasis' : 'Outreach-Brief gesendet')}
      </p>
    </button>
  );
}

function Fact({ label, value, testId }) {
  if (!value) return null;
  return (
    <div className="border border-border/60 bg-secondary/30 rounded-sm px-3 py-2" data-testid={testId}>
      <p className="text-[10px] text-muted-foreground font-mono uppercase">{label}</p>
      <p className="text-sm text-foreground mt-0.5 leading-snug">{value}</p>
    </div>
  );
}

export function InboundCallDialog({ open, onClose, initialItem }) {
  const [item, setItem] = useState(null);
  const [q, setQ] = useState('');
  const { results, searching } = useInboundSearch(q);
  const [brief, setBrief] = useState(null);
  const [briefError, setBriefError] = useState(false);
  const [guide, setGuide] = useState(null);
  const [guideLoading, setGuideLoading] = useState(false);
  const [guideError, setGuideError] = useState('');
  const [aiExtra, setAiExtra] = useState({ description: '', location: '' });
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) { setItem(initialItem || null); setQ(''); }
  }, [open, initialItem]);

  useEffect(() => {
    if (!item) { setBrief(null); setGuide(null); return; }
    setBrief(null); setGuide(null); setBriefError(false); setGuideError('');
    setAiExtra({ description: '', location: '' }); setNote('');
    api.inboundCallBriefing(item.id).then(r => {
      setBrief(r.data);
      setAiExtra({ description: r.data.cached_description || '', location: r.data.cached_location || '' });
      if (r.data.cached_guide) setGuide(r.data.cached_guide);
      else loadGuide(item.id, false);
    }).catch(() => setBriefError(true));
  }, [item]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGuide = async (key, force) => {
    setGuideLoading(true); setGuideError('');
    try {
      const r = await api.inboundCallBullets(key, force);
      setGuide({ opener: r.data.opener || [], questions: r.data.questions || [], next_step: r.data.next_step || '' });
      setAiExtra(x => ({
        description: r.data.description || x.description,
        location: r.data.location || x.location,
      }));
    } catch (err) {
      setGuide(null);
      setGuideError(err.response?.data?.detail || 'KI-Leitfaden fehlgeschlagen — bitte erneut versuchen');
    }
    setGuideLoading(false);
  };

  const dealId = brief?.letter?.project_id || brief?.project?.id;

  const flushNote = () => {
    const text = note.trim();
    if (!text) return;
    setNote('');
    if (!dealId) { toast.error('Kein Deal verknüpft — Notiz konnte nicht gespeichert werden'); return; }
    const stamp = new Date().toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    api.addComment(dealId, `📞 Inbound-Call-Notiz (${stamp}):\n${text}`)
      .then(() => toast.success('Call-Notiz am Deal gespeichert'))
      .catch(() => toast.error('Notiz konnte nicht gespeichert werden'));
  };

  const handleClose = () => { flushNote(); onClose(); };
  const handleBack = () => { flushNote(); setItem(null); };
  const navigate = useNavigate();
  const openDealPage = () => {
    if (!dealId) return;
    flushNote();
    onClose();
    navigate(`/projects/${dealId}`);
  };

  const letter = brief?.letter;
  const proj = brief?.project;
  const es = proj?.executive_summary || {};
  const fin = brief?.latest_financials;
  const location = proj?.location || aiExtra.location;
  const description = aiExtra.description || proj?.company_overview;
  const website = (proj?.company_website || '').trim();
  const websiteHost = website ? website.replace(/^https?:\/\//, '').replace(/\/$/, '') : '';
  const firstContact = (brief?.contacts || [])[0];
  const whoName = letter?.recipient_name
    || (firstContact ? `${firstContact.name}${firstContact.role ? ` (${firstContact.role})` : ''}` : 'Inhaber / Broker');

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="bg-card border-border rounded-sm w-[calc(100vw-1.25rem)] sm:max-w-lg max-h-[92vh] overflow-y-auto p-4 sm:p-6"
        data-testid="inbound-call-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading text-foreground flex items-center gap-2">
            {item ? (
              <button onClick={handleBack} data-testid="inbound-back-btn"
                className="p-1 -ml-1 text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </button>
            ) : <PhoneIncoming className="w-5 h-5 text-emerald-400" />}
            {item ? (item.company_name || 'Briefing') : 'Inbound Call'}
          </DialogTitle>
        </DialogHeader>

        {!item ? (
          <>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Firmenname eintippen…"
                data-testid="inbound-dialog-search"
                className="w-full bg-secondary border border-border rounded-sm pl-9 pr-3 h-12 text-base text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-emerald-500/50" />
            </div>
            {q.trim().length < 2 ? (
              <p className="text-xs text-muted-foreground text-center py-6">
                Mindestens 2 Buchstaben — durchsucht Brief-Empfänger und Broker-Deals.
              </p>
            ) : searching && !results ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
            ) : (results || []).length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6" data-testid="inbound-no-results">
                Keine passende Firma gefunden.
              </p>
            ) : (
              <div className="border border-border/60 rounded-sm overflow-hidden" data-testid="inbound-dialog-results">
                {results.map((r, i) => <ResultRow key={r.id} r={r} i={i} big onSelect={setItem} />)}
              </div>
            )}
          </>
        ) : briefError ? (
          <p className="text-sm text-amber-400 text-center py-6" data-testid="inbound-brief-error">
            Briefing konnte nicht geladen werden — bitte erneut versuchen.
          </p>
        ) : !brief ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-3" data-testid="inbound-brief">
            <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm px-3 py-3" data-testid="inbound-who">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[10px] text-emerald-300/80 font-mono uppercase flex items-center gap-1.5">
                    <User className="w-3 h-3" /> {letter ? 'Wer ruft an' : 'Gesprächspartner'}
                  </p>
                  <p className="text-base font-semibold text-foreground mt-1">{whoName}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5">
                    {letter?.company_name || proj?.name}
                    {location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{location}</span>}
                  </p>
                </div>
                {dealId && (
                  <button onClick={openDealPage} data-testid="inbound-open-deal"
                    className="shrink-0 flex items-center gap-1 text-[11px] text-emerald-300 hover:text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/10 rounded-sm px-2 py-1 transition-colors">
                    Deal öffnen <ExternalLink className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Fact label="Ort" value={location} testId="inbound-fact-location" />
              <Fact label="Branche" value={proj?.sector} testId="inbound-fact-sector" />
              <Fact label="Stage" value={STAGE_LABELS[proj?.pipeline_stage] || proj?.pipeline_stage} testId="inbound-fact-stage" />
              <Fact label={`Umsatz ${fin?.year || ''}`} value={fmtM(fin?.revenue)} testId="inbound-fact-revenue" />
              <Fact label={`EBITDA ${fin?.year || ''}`} value={fmtM(fin?.ebitda) || proj?.ebitda_range} testId="inbound-fact-ebitda" />
              <Fact label="Deal-Typ" value={DEAL_TYPE_LABELS[proj?.deal_type] || proj?.deal_type} testId="inbound-fact-dealtype" />
              <Fact label="Briefe gesendet" value={brief.letters_sent > 0 ? String(brief.letters_sent) : null} testId="inbound-fact-letters" />
              <Fact label="Mitarbeiter" value={es.employees} testId="inbound-fact-employees" />
              <Fact label="Gegründet" value={es.founding_date} testId="inbound-fact-founded" />
              <Fact label="OLTA-Verantwortlich" value={proj?.owner_name} testId="inbound-fact-owner" />
              <Fact label="Website" testId="inbound-fact-website"
                value={website ? (
                  <a href={website.startsWith('http') ? website : `https://${website}`} target="_blank" rel="noreferrer"
                    className="text-sky-400 hover:underline break-all">{websiteHost}</a>
                ) : null} />
            </div>

            {letter ? (
              <div className="border border-border/60 bg-secondary/30 rounded-sm px-3 py-2 flex items-start gap-2" data-testid="inbound-history">
                <Send className="w-3.5 h-3.5 text-sky-400 mt-0.5 shrink-0" />
                <p className="text-xs text-foreground/85 leading-snug">
                  Brief gesendet am <span className="font-medium">{fmtDate(letter?.sent_at) || '—'}</span>
                  {letter?.status === 'responded' && <span className="text-emerald-300"> · hat bereits schriftlich reagiert{letter?.responded_at ? ` (${fmtDate(letter.responded_at)})` : ''}</span>}
                  {letter?.status === 'sent' && <span className="text-muted-foreground"> · bisher keine schriftliche Reaktion</span>}
                </p>
              </div>
            ) : proj?.deal_type === 'brokered' && (
              <div className="border border-border/60 bg-secondary/30 rounded-sm px-3 py-2 flex items-start gap-2" data-testid="inbound-brokered-hint">
                <Briefcase className="w-3.5 h-3.5 text-violet-400 mt-0.5 shrink-0" />
                <p className="text-xs text-foreground/85 leading-snug">
                  Brokered Deal — Leitfragen nutzen den Teaser und die Scorecard-Lücken als Basis.
                </p>
              </div>
            )}

            {(brief.recent_activity || []).length > 0 && (
              <div className="border border-border/60 bg-secondary/30 rounded-sm px-3 py-2" data-testid="inbound-recent-activity">
                <p className="text-[10px] text-muted-foreground font-mono uppercase flex items-center gap-1.5">
                  <History className="w-3 h-3" /> Letzte Aktivität am Deal
                </p>
                <div className="mt-1.5 space-y-1">
                  {brief.recent_activity.map((a, i) => (
                    <p key={i} className="text-[11px] text-foreground/80 leading-snug" data-testid={`inbound-activity-${i}`}>
                      <span className="text-muted-foreground/60">{fmtDate(a.at)}</span>
                      {a.user_name ? <span className="text-muted-foreground"> · {a.user_name}</span> : null}
                      {' · '}{(a.detail || '').slice(0, 90)}{(a.detail || '').length > 90 ? '…' : ''}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {description ? (
              <p className="text-xs text-foreground/80 leading-relaxed" data-testid="inbound-description">
                {description}
              </p>
            ) : guideLoading ? (
              <div className="h-3.5 bg-secondary/80 rounded animate-pulse w-4/5" />
            ) : null}

            <div className="border border-violet-500/30 bg-violet-500/5 rounded-sm px-3 py-3" data-testid="inbound-guide">
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-violet-300/90 font-mono uppercase flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> Für das Gespräch · Zielkorridor-Check
                </p>
                <button onClick={() => loadGuide(item.id, true)} disabled={guideLoading}
                  data-testid="inbound-regen-guide"
                  className="p-1 text-muted-foreground/60 hover:text-violet-300 transition-colors" title="Neu generieren">
                  <RefreshCw className={`w-3.5 h-3.5 ${guideLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              {guideLoading ? (
                <div className="space-y-2 mt-2" data-testid="inbound-guide-loading">
                  {[0, 1, 2, 3].map(i => (
                    <div key={i} className="h-3.5 bg-secondary/80 rounded animate-pulse" style={{ width: `${88 - i * 9}%` }} />
                  ))}
                </div>
              ) : !guide ? (
                <p className="text-xs text-amber-400/90 mt-2" data-testid="inbound-guide-error">
                  {guideError || 'Kein KI-Leitfaden verfügbar — Symbol oben rechts für neuen Versuch.'}
                </p>
              ) : (
                <div className="mt-2">
                  <GuideContent guide={guide} />
                </div>
              )}
            </div>

            <div>
              <label className="text-[10px] text-muted-foreground font-mono uppercase flex items-center gap-1.5 mb-1">
                <NotebookPen className="w-3 h-3" /> Call-Notizen · werden beim Schließen automatisch am Deal gespeichert
              </label>
              <Textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
                placeholder="Kurz mitschreiben…" data-testid="inbound-call-note"
                className="bg-secondary border-border text-foreground rounded-sm text-base sm:text-sm leading-snug" />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function InboundCallSidebar() {
  const [q, setQ] = useState('');
  const { results, searching } = useInboundSearch(q);
  const [item, setItem] = useState(null);

  return (
    <div className="mb-2 relative">
      <div className="relative">
        <PhoneIncoming className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-emerald-400" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Inbound Call: Firma…"
          data-testid="inbound-call-search"
          className="w-full bg-secondary/40 border border-border rounded-sm pl-8 pr-2 h-9 text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-emerald-500/50 transition-colors" />
      </div>
      {q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-10 bg-card border border-border rounded-sm shadow-xl z-50 max-h-72 overflow-y-auto"
          data-testid="inbound-call-dropdown">
          {searching && !results ? (
            <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
          ) : (results || []).length === 0 ? (
            <p className="text-[11px] text-muted-foreground text-center py-4 px-2">Keine passende Firma gefunden.</p>
          ) : (
            results.map((r, i) => <ResultRow key={r.id} r={r} i={i} onSelect={(sel) => { setItem(sel); setQ(''); }} />)
          )}
        </div>
      )}
      <InboundCallDialog open={!!item} initialItem={item} onClose={() => setItem(null)} />
    </div>
  );
}
